/**
 * Client-safe public HTTPS origin only. Rejects credentials, non-HTTPS schemes,
 * IPs, localhost, ports, and unbounded hosts. Never used for API tokens.
 */

const HOST =
  /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/i;

export function parsePublicHttpsOrigin(raw: string | null | undefined): string | null {
  const s = String(raw ?? "").trim();
  if (!s || s.length > 253) return null;
  if (/[\s\\]/.test(s)) return null;
  if (/^(javascript|data|file|vbscript):/i.test(s)) return null;
  let url: URL;
  try {
    url = new URL(s);
  } catch {
    return null;
  }
  if (url.protocol !== "https:") return null;
  if (url.username || url.password) return null;
  if (url.port && url.port !== "443") return null;
  const host = url.hostname.toLowerCase();
  if (!HOST.test(host)) return null;
  if (host === "localhost" || host.endsWith(".localhost")) return null;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null;
  return `https://${host}`;
}

export function parseStorefrontProductPath(raw: string | null | undefined): string | null {
  const s = String(raw ?? "").trim();
  if (!s.startsWith("/") || s.startsWith("//")) return null;
  if (s.includes("://") || s.includes("\\") || s.includes("..") || s.includes("@")) return null;
  if (!/^\/[a-zA-Z0-9][a-zA-Z0-9/_-]*\/?$/.test(s)) return null;
  return s;
}

function unwrapStorePayload(payload: unknown): Record<string, unknown> | null {
  const root = payload && typeof payload === "object" && !Array.isArray(payload)
    ? (payload as Record<string, unknown>)
    : null;
  if (!root) return null;
  const nested = root.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }
  return root;
}

/** Extract a public storefront origin from BigCommerce GET /v2/store metadata. */
export function originFromBcStorePayload(payload: unknown): string | null {
  const data = unwrapStorePayload(payload);
  if (!data) return null;
  const secure = parsePublicHttpsOrigin(typeof data.secure_url === "string" ? data.secure_url : null);
  if (secure) return secure;
  const domainRaw = typeof data.domain === "string" ? data.domain.trim() : "";
  if (!domainRaw) return null;
  if (domainRaw.includes("://")) return parsePublicHttpsOrigin(domainRaw);
  return parsePublicHttpsOrigin(`https://${domainRaw}`);
}

export function extractBcCustomUrl(payload: unknown): string | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const root = payload as Record<string, unknown>;
  const nested = root.data && typeof root.data === "object" && !Array.isArray(root.data)
    ? (root.data as Record<string, unknown>)
    : root;
  const custom = nested.custom_url;
  if (custom && typeof custom === "object" && !Array.isArray(custom)) {
    const url = (custom as Record<string, unknown>).url;
    if (typeof url === "string") return parseStorefrontProductPath(url);
  }
  return null;
}
